import React, { useEffect, useRef } from "react";

// Lightweight canvas "market motion" loop: scrolling candlesticks + gridlines behind the hero.
export default function MarketMotion({ className, speed = 0.32 }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, dpr;
    const step = 22;
    const cw = 13;
    let candles = [];
    let price = 50;
    let offset = 0;

    const gen = () => {
      const open = price;
      const drift = (Math.random() - 0.48) * 7;
      const close = Math.max(12, Math.min(88, open + drift));
      const high = Math.max(open, close) + Math.random() * 5;
      const low = Math.min(open, close) - Math.random() * 5;
      price = close;
      return { open, close, high, low };
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth || canvas.offsetWidth || 1200;
      h = canvas.clientHeight || canvas.offsetHeight || 520;
      canvas.width = Math.max(1, w * dpr);
      canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const need = Math.ceil(w / step) + 3;
      while (candles.length < need) candles.push(gen());
    };
    resize();
    window.addEventListener("resize", resize);

    const toY = (v) => h - (v / 100) * h;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = "rgba(216,174,78,0.06)";
      ctx.lineWidth = 1;
      for (let g = 0; g <= 4; g++) {
        const gy = (h / 4) * g;
        ctx.beginPath();
        ctx.moveTo(0, gy);
        ctx.lineTo(w, gy);
        ctx.stroke();
      }
      for (let i = 0; i < candles.length; i++) {
        const c = candles[i];
        const x = i * step - offset;
        const up = c.close >= c.open;
        const stroke = up ? "rgba(52,211,153,0.45)" : "rgba(248,113,113,0.45)";
        const fill = up ? "rgba(52,211,153,0.22)" : "rgba(248,113,113,0.22)";
        ctx.strokeStyle = stroke;
        ctx.fillStyle = fill;
        ctx.beginPath();
        ctx.moveTo(x + cw / 2, toY(c.high));
        ctx.lineTo(x + cw / 2, toY(c.low));
        ctx.stroke();
        const yO = toY(c.open);
        const yC = toY(c.close);
        const top = Math.min(yO, yC);
        const bh = Math.max(2, Math.abs(yC - yO));
        ctx.fillRect(x, top, cw, bh);
        ctx.strokeRect(x, top, cw, bh);
      }
      offset += speed;
      if (offset >= step) {
        offset -= step;
        candles.shift();
        candles.push(gen());
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [speed]);
  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
